import { fetchJSON as fetchAPI } from './client';

export type ProcurementPlan = {
  id: string;
  name: string;
  status: string;
  budget_amount: number;
  planned_spend: number;
  procurement_savings: number;
  health_score: number;
  supplier_dependency_index: number;
  container_utilization: number;
  cash_flow_impact: number;
  lead_time_reliability_score: number;
};

export type PurchaseRequirement = {
  id: string;
  plan_id: string;
  gross_requirement: number;
  net_requirement: number;
  planned_order_qty: number;
  eoq: number;
  total_cost: number;
  container_fill_pct: number;
  required_delivery_date: string;
  explanation: string;
};

export type SupplierPlan = {
  id: string;
  supplier_id: string;
  supplier_name: string;
  lead_time_days: number;
  capacity_units: number;
  allocated_units: number;
  utilization_pct: number;
  supplier_score: number;
  reliability_score: number;
  risk_rating: number;
  preferred_supplier: boolean;
  strategic_supplier: boolean;
};

export type ProcurementRisk = {
  id: string;
  risk_type: string;
  severity: string;
  probability: number;
  business_impact: string;
  recommended_mitigation: string;
};

export type ProcurementRecommendation = {
  id: string;
  type: string;
  title: string;
  description: string;
  business_explanation: string;
  confidence: number;
  expected_savings: number;
  cash_flow_impact: number;
  auto_execute: boolean;
  status: string;
};

export type ProcurementScenario = {
  id: string;
  name: string;
  scenario_type: string;
  total_cost: number;
  lead_time_days: number;
  risk_score: number;
  supplier_utilization: number;
  description?: string;
};

export type ProcurementDashboard = {
  active_plans: number;
  planned_spend: number;
  procurement_savings: number;
  budget_variance: number;
  avg_lead_time_days: number;
  avg_supplier_score: number;
  open_risks: number;
  pending_recommendations: number;
  health_score: number;
  supplier_dependency_index: number;
  container_utilization: number;
};

const API_BASE = import.meta.env.VITE_PROCUREMENTPLANNING_API ?? '/api/v1';

function procFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const procurementPlanningApi = {
  dashboard: () => procFetch<ProcurementDashboard>('/procurement-dashboard'),
  listPlans: () => procFetch<ProcurementPlan[]>('/procurement-plans'),
  listRequirements: (planId?: string) => procFetch<PurchaseRequirement[]>(`/purchase-requirements${planId ? `?plan_id=${planId}` : ''}`),
  listSupplierPlans: (planId?: string) => procFetch<SupplierPlan[]>(`/supplier-plans${planId ? `?plan_id=${planId}` : ''}`),
  listRisks: (planId?: string) => procFetch<ProcurementRisk[]>(`/procurement-risks${planId ? `?plan_id=${planId}` : ''}`),
  listRecommendations: (planId?: string) => procFetch<ProcurementRecommendation[]>(`/procurement-recommendations${planId ? `?plan_id=${planId}` : ''}`),
  listScenarios: (planId?: string) => procFetch<ProcurementScenario[]>(`/procurement-scenarios${planId ? `?plan_id=${planId}` : ''}`),
  approvePlan: (id: string) => procFetch<ProcurementPlan>(`/procurement-plans/${id}/approve`, { method: 'POST' }),
};
